/**
 * Hook personnalisé pour détecter si un tableau nécessite un affichage responsive
 *
 * Ce hook combine deux critères :
 * 1. Détection du breakpoint MUI (mobile/tablette)
 * 2. Détection du scroll horizontal (viewport trop étroit pour afficher le tableau)
 *
 * Si l'un des deux critères est rempli, le mode card est activé pour améliorer l'UX.
 *
 * @module useTableResponsive
 * @author Équipe Brydge
 * @since 2.0.0
 */

import {useState, useEffect} from 'react';
import {useTheme} from '@mui/material/styles';
import {useMediaQuery} from '@mui/material';

/**
 * Hook pour détecter automatiquement si le mode responsive (cards) doit être activé
 *
 * Cas d'activation du mode card :
 * - Écran mobile/tablette portrait (< breakpoint)
 * - Tablette paysage où le tableau nécessite un scroll horizontal
 * - Desktop avec fenêtre réduite
 *
 * @param {Object} options - Options du hook
 * @param {string} options.breakpoint - Breakpoint MUI ('xs', 'sm', 'md', 'lg', 'xl')
 * @param {number} options.minTableWidth - Largeur minimale du tableau en px
 * @param {number} options.margin - Marge de sécurité en px (padding, scrollbar, etc.)
 * @return {Object} État responsive
 * @return {boolean} returns.isMobile - true si mode card doit être activé
 * @return {boolean} returns.needsScroll - true si scroll horizontal serait nécessaire
 * @return {boolean} returns.isBreakpoint - true si sous le breakpoint MUI
 *
 * @example
 * // Utilisation basique avec détection automatique
 * const {isMobile} = useTableResponsive({
 *   minTableWidth: 960,
 *   breakpoint: 'md'
 * });
 *
 * @example
 * // Utilisation avec détails de diagnostic
 * const {isMobile, needsScroll, isBreakpoint} = useTableResponsive({
 *   minTableWidth: 1200,
 *   breakpoint: 'lg',
 *   margin: 100
 * });
 *
 * if (needsScroll) {
 *   console.log('Mode card activé : tableau trop large pour viewport');
 * }
 */
export function useTableResponsive({
  breakpoint = 'md',
  minTableWidth = 960,
  margin = 80, // Padding container + scrollbar + marge sécurité
} = {}) {
  const theme = useTheme();

  // Détection du breakpoint MUI classique
  const isBreakpoint = useMediaQuery(theme.breakpoints.down(breakpoint));

  // État pour détecter si scroll horizontal nécessaire
  const [needsScroll, setNeedsScroll] = useState(false);

  useEffect(() => {
    const checkScrollNeed = () => {
      // Largeur disponible pour afficher le contenu
      const availableWidth = window.innerWidth - margin;

      // Si le tableau ne rentre pas dans l'espace disponible, on passe en mode card
      setNeedsScroll(availableWidth < minTableWidth);
    };

    // Vérification initiale
    checkScrollNeed();

    // Écoute du redimensionnement de la fenêtre
    window.addEventListener('resize', checkScrollNeed);

    // Nettoyage
    return () => window.removeEventListener('resize', checkScrollNeed);
  }, [minTableWidth, margin]);

  // Mode mobile activé si :
  // - Sous le breakpoint MUI (mobile/tablette portrait)
  // - OU viewport trop étroit pour afficher le tableau sans scroll
  const isMobile = isBreakpoint || needsScroll;

  return {
    isMobile,
    needsScroll,
    isBreakpoint,
  };
}

/**
 * Hook simplifié qui retourne uniquement le booléen isMobile
 * Équivalent à useMediaQuery mais avec détection du scroll horizontal
 *
 * @param {Object} options - Options du hook
 * @param {string} options.breakpoint - Breakpoint MUI
 * @param {number} options.minTableWidth - Largeur minimale du tableau
 * @return {boolean} true si mode card doit être activé
 *
 * @example
 * const isMobile = useTableResponsiveSimple({minTableWidth: 960});
 */
export function useTableResponsiveSimple(options) {
  const {isMobile} = useTableResponsive(options);
  return isMobile;
}

export default useTableResponsive;
